import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_OPTIONS, formatCategory } from '../constants/categories';
import { CATEGORY_GROUPS } from '../constants/categoryGroups';

const STATE_METROS: Record<string, { label: string; cities: string[] }> = {
  LA: { label: 'Louisiana', cities: ['Baton Rouge, LA', 'New Orleans, LA', 'Shreveport, LA', 'Lafayette, LA', 'Lake Charles, LA'] },
  TX: { label: 'Texas', cities: ['Houston, TX', 'Dallas, TX', 'San Antonio, TX', 'Austin, TX', 'Fort Worth, TX'] },
  FL: { label: 'Florida', cities: ['Miami, FL', 'Orlando, FL', 'Tampa, FL', 'Jacksonville, FL', 'Fort Lauderdale, FL'] },
  GA: { label: 'Georgia', cities: ['Atlanta, GA', 'Augusta, GA', 'Columbus, GA', 'Savannah, GA', 'Athens, GA'] },
  MS: { label: 'Mississippi', cities: ['Jackson, MS', 'Gulfport, MS', 'Southaven, MS', 'Hattiesburg, MS', 'Biloxi, MS'] },
  AL: { label: 'Alabama', cities: ['Birmingham, AL', 'Montgomery, AL', 'Huntsville, AL', 'Mobile, AL', 'Tuscaloosa, AL'] },
  TN: { label: 'Tennessee', cities: ['Nashville, TN', 'Memphis, TN', 'Knoxville, TN', 'Chattanooga, TN', 'Clarksville, TN'] },
  NC: { label: 'North Carolina', cities: ['Charlotte, NC', 'Raleigh, NC', 'Greensboro, NC', 'Durham, NC', 'Winston-Salem, NC'] },
  SC: { label: 'South Carolina', cities: ['Columbia, SC', 'Charleston, SC', 'Greenville, SC', 'Spartanburg, SC', 'Rock Hill, SC'] },
  AR: { label: 'Arkansas', cities: ['Little Rock, AR', 'Fort Smith, AR', 'Fayetteville, AR', 'Springdale, AR', 'Jonesboro, AR'] },
  OK: { label: 'Oklahoma', cities: ['Oklahoma City, OK', 'Tulsa, OK', 'Norman, OK', 'Broken Arrow, OK', 'Lawton, OK'] },
  CA: { label: 'California', cities: ['Los Angeles, CA', 'San Diego, CA', 'San Jose, CA', 'San Francisco, CA', 'Fresno, CA'] },
  NY: { label: 'New York', cities: ['New York City, NY', 'Buffalo, NY', 'Rochester, NY', 'Yonkers, NY', 'Syracuse, NY'] },
  IL: { label: 'Illinois', cities: ['Chicago, IL', 'Aurora, IL', 'Naperville, IL', 'Joliet, IL', 'Rockford, IL'] },
  OH: { label: 'Ohio', cities: ['Columbus, OH', 'Cleveland, OH', 'Cincinnati, OH', 'Toledo, OH', 'Akron, OH'] },
  PA: { label: 'Pennsylvania', cities: ['Philadelphia, PA', 'Pittsburgh, PA', 'Allentown, PA', 'Erie, PA', 'Reading, PA'] },
  MI: { label: 'Michigan', cities: ['Detroit, MI', 'Grand Rapids, MI', 'Warren, MI', 'Sterling Heights, MI', 'Lansing, MI'] },
  AZ: { label: 'Arizona', cities: ['Phoenix, AZ', 'Tucson, AZ', 'Mesa, AZ', 'Chandler, AZ', 'Scottsdale, AZ'] },
  CO: { label: 'Colorado', cities: ['Denver, CO', 'Colorado Springs, CO', 'Aurora, CO', 'Fort Collins, CO', 'Lakewood, CO'] },
  WA: { label: 'Washington', cities: ['Seattle, WA', 'Spokane, WA', 'Tacoma, WA', 'Vancouver, WA', 'Bellevue, WA'] },
};

const STATE_PRESETS: Record<string, string> = Object.fromEntries(
  Object.entries(STATE_METROS).map(([code, info]) => [code, info.cities.join("; ")])
);


const METRO_SUBURBS: Record<string, { metro: string; cities: string[] }> = {
  LA: { metro: 'Baton Rouge', cities: ['Denham Springs, LA', 'Zachary, LA', 'Prairieville, LA', 'Gonzales, LA', 'Walker, LA'] },
  TX: { metro: 'Houston', cities: ['Sugar Land, TX', 'The Woodlands, TX', 'Pearland, TX', 'Pasadena, TX', 'League City, TX'] },
  FL: { metro: 'Miami', cities: ['Fort Lauderdale, FL', 'Coral Springs, FL', 'Pembroke Pines, FL', 'Hollywood, FL', 'Miramar, FL'] },
  GA: { metro: 'Atlanta', cities: ['Sandy Springs, GA', 'Marietta, GA', 'Roswell, GA', 'Alpharetta, GA', 'Smyrna, GA'] },
  MS: { metro: 'Jackson', cities: ['Ridgeland, MS', 'Madison, MS', 'Brandon, MS', 'Flowood, MS', 'Pearl, MS'] },
  AL: { metro: 'Birmingham', cities: ['Hoover, AL', 'Vestavia Hills, AL', 'Homewood, AL', 'Bessemer, AL', 'Trussville, AL'] },
  TN: { metro: 'Nashville', cities: ['Brentwood, TN', 'Franklin, TN', 'Murfreesboro, TN', 'Hendersonville, TN', 'Smyrna, TN'] },
  NC: { metro: 'Charlotte', cities: ['Concord, NC', 'Gastonia, NC', 'Huntersville, NC', 'Matthews, NC', 'Mooresville, NC'] },
  SC: { metro: 'Columbia', cities: ['Lexington, SC', 'Irmo, SC', 'West Columbia, SC', 'Cayce, SC', 'Chapin, SC'] },
  AR: { metro: 'Little Rock', cities: ['North Little Rock, AR', 'Conway, AR', 'Benton, AR', 'Bryant, AR', 'Sherwood, AR'] },
  OK: { metro: 'Oklahoma City', cities: ['Edmond, OK', 'Norman, OK', 'Moore, OK', 'Yukon, OK', 'Midwest City, OK'] },
  CA: { metro: 'Los Angeles', cities: ['Long Beach, CA', 'Glendale, CA', 'Anaheim, CA', 'Burbank, CA', 'Pasadena, CA'] },
  NY: { metro: 'New York City', cities: ['Yonkers, NY', 'New Rochelle, NY', 'Mount Vernon, NY', 'White Plains, NY', 'Hempstead, NY'] },
  IL: { metro: 'Chicago', cities: ['Naperville, IL', 'Aurora, IL', 'Elgin, IL', 'Joliet, IL', 'Waukegan, IL'] },
  OH: { metro: 'Columbus', cities: ['Dublin, OH', 'Westerville, OH', 'Grove City, OH', 'Hilliard, OH', 'Gahanna, OH'] },
  PA: { metro: 'Philadelphia', cities: ['Norristown, PA', 'Reading, PA', 'Allentown, PA', 'Chester, PA', 'Levittown, PA'] },
  MI: { metro: 'Detroit', cities: ['Warren, MI', 'Sterling Heights, MI', 'Livonia, MI', 'Ann Arbor, MI', 'Dearborn, MI'] },
  AZ: { metro: 'Phoenix', cities: ['Scottsdale, AZ', 'Mesa, AZ', 'Tempe, AZ', 'Chandler, AZ', 'Gilbert, AZ'] },
  CO: { metro: 'Denver', cities: ['Aurora, CO', 'Lakewood, CO', 'Thornton, CO', 'Westminster, CO', 'Arvada, CO'] },
  WA: { metro: 'Seattle', cities: ['Bellevue, WA', 'Renton, WA', 'Kirkland, WA', 'Redmond, WA', 'Kent, WA'] },
};

const BR_METRO_PRESET = 'Baton Rouge, LA; Denham Springs, LA; Zachary, LA; Prairieville, LA; Gonzales, LA; Livingston, LA; Walker, LA; Central, LA; Baker, LA; Plaquemine, LA';

const SAVED_REGIONS = [
  'Baton Rouge, LA',
  'Lake Charles, LA',
  'Shreveport, LA',
  'New Orleans, LA',
  'Alexandria, LA',
  'Lafayette, LA',
  'Monroe, LA',
  'Denham Springs, LA',
];

const API = 'https://quotebid-backend-production.up.railway.app';

interface Prospect {
  businessName: string;
  ownerFirst: string;
  ownerLast: string;
  email: string;
  phone: string;
  website: string;
  category: string;
  region: string;
  source: string;
  address: string;
  selected?: boolean;
  emailStatus?: 'verified' | 'risky' | 'invalid' | 'pending' | '';
  note?: string;
  isDuplicate?: boolean;
}

type RunMode = 'single' | 'state';

export default function ProspectsPage() {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([...CATEGORY_OPTIONS]);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [runMode, setRunMode] = useState<RunMode>('single');
  const [region, setRegion] = useState('Baton Rouge, LA');
  const [selectedState, setSelectedState] = useState('LA');
  const [batchSize, setBatchSize] = useState(10);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(false);
  const [runAllLoading, setRunAllLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyProgress, setVerifyProgress] = useState('');
  const [runAllProgress, setRunAllProgress] = useState('');
  const [runAllProgressPct, setRunAllProgressPct] = useState(0);
  const [currentCity, setCurrentCity] = useState('');
  const [exported, setExported] = useState(0);
  const [error, setError] = useState('');
  const [emailsOnly, setEmailsOnly] = useState(false);
  const [hunterCredits, setHunterCredits] = useState<number | null>(null);
  const [importedEmails, setImportedEmails] = useState<Set<string>>(new Set());
  const [editingNote, setEditingNote] = useState<number | null>(null);
  const [noteText, setNoteText] = useState('');

  const getToken = () => localStorage.getItem('token') || '';

  // Parse cities from semicolon-separated input
  const parseCities = (input: string): string[] => {
    return input.split(';').map(c => c.trim()).filter(c => c.length > 0);
  };

  const cities = parseCities(region);
  const isMultiCity = cities.length > 1;

  useEffect(() => {
    fetch(`${API}/api/prospects/hunter-credits`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }).then(r => r.json()).then(d => setHunterCredits(d.credits)).catch(() => {});
  }, []);

  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const selectAllCategories = () => setSelectedCategories([...CATEGORY_OPTIONS]);
  const clearAllCategories = () => setSelectedCategories([]);

  const selectGroup = (groupId: string) => {
    const group = CATEGORY_GROUPS.find(g => g.id === groupId);
    if (group) setSelectedCategories([...group.slugs]);
  };

  const mergeGroup = (groupId: string) => {
    const group = CATEGORY_GROUPS.find(g => g.id === groupId);
    if (!group) return;
    setSelectedCategories(prev => Array.from(new Set([...prev, ...group.slugs])));
  };

  const clearGroup = (groupId: string) => {
    const group = CATEGORY_GROUPS.find(g => g.id === groupId);
    if (!group) return;
    const slugSet = new Set<string>(group.slugs);
    setSelectedCategories(prev => prev.filter(c => !slugSet.has(c)));
  };

  const runForCity = async (cityRegion: string, allProspects: Prospect[]) => {
    for (let i = 0; i < selectedCategories.length; i++) {
      const cat = selectedCategories[i];
      setRunAllProgress(`Searching ${cityRegion} — ${i + 1}/${selectedCategories.length}: ${formatCategory(cat)}...`);
      try {
        const res = await fetch(`${API}/api/prospects/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ category: cat, region: cityRegion, batchSize }),
        });
        const data = await res.json();
        if (res.ok && data.prospects) {
          const withSelected = data.prospects.map((p: Prospect) => ({
            ...p, selected: true, emailStatus: '', note: '',
            isDuplicate: p.email ? importedEmails.has(p.email.toLowerCase()) : false,
          }));
          allProspects.push(...withSelected);
          setProspects([...allProspects]);
        }
      } catch (e) { console.error('Run city error:', e); continue; }
      await new Promise(r => setTimeout(r, 500));
    }
  };

  const runSelectedCategories = async () => {
    if (selectedCategories.length === 0) { setError('Please select at least one category'); return; }

    setRunAllLoading(true);
    setRunAllProgress('');
    setRunAllProgressPct(0);
    setError('');
    setProspects([]);

    const allProspects: Prospect[] = [];

    if (runMode === 'single') {
      const citiesToRun = parseCities(region);

      if (citiesToRun.length === 1) {
        // Single city mode — original behavior
        const total = selectedCategories.length;
        for (let i = 0; i < total; i++) {
          const cat = selectedCategories[i];
          setRunAllProgress(`Running ${i + 1} of ${total}: ${formatCategory(cat)}...`);
          setRunAllProgressPct(Math.round((i / total) * 100));
          try {
            const res = await fetch(`${API}/api/prospects/generate`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify({ category: cat, region: citiesToRun[0], batchSize }),
            });
            const data = await res.json();
            if (res.ok && data.prospects) {
              const withSelected = data.prospects.map((p: Prospect) => ({
                ...p, selected: true, emailStatus: '', note: '',
                isDuplicate: p.email ? importedEmails.has(p.email.toLowerCase()) : false,
              }));
              allProspects.push(...withSelected);
              setProspects([...allProspects]);
            }
          } catch (e) { console.error('Run error:', e); continue; }
          await new Promise(r => setTimeout(r, 500));
        }
      } else {
        // Multi-city mode — loop through each city
        for (let c = 0; c < citiesToRun.length; c++) {
          const city = citiesToRun[c];
          setCurrentCity(city);
          setRunAllProgressPct(Math.round((c / citiesToRun.length) * 100));
          await runForCity(city, allProspects);
        }
      }

      const label = citiesToRun.length > 1
        ? `${citiesToRun.length} cities × ${selectedCategories.length} categories`
        : `${selectedCategories.length} categories`;
      setRunAllProgress(`Complete! ${allProspects.length} prospects across ${label}.`);

    } else {
      // Full state mode
      const stateCities = STATE_METROS[selectedState]?.cities || [];
      for (let c = 0; c < stateCities.length; c++) {
        const city = stateCities[c];
        setCurrentCity(city);
        setRunAllProgressPct(Math.round((c / stateCities.length) * 100));
        await runForCity(city, allProspects);
      }
      setRunAllProgress(`Complete! ${allProspects.length} prospects across ${STATE_METROS[selectedState]?.cities.length} cities × ${selectedCategories.length} categories.`);
    }

    setRunAllProgressPct(100);
    setCurrentCity('');
    setRunAllLoading(false);

    fetch(`${API}/api/prospects/hunter-credits`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    }).then(r => r.json()).then(d => setHunterCredits(d.credits)).catch(() => {});
  };

  const verifyAllEmails = async () => {
    const emailProspects = prospects.filter(p => p.email);
    if (emailProspects.length === 0) return;
    setVerifyLoading(true);
    setVerifyProgress(`Verifying 0 of ${emailProspects.length} emails...`);
    let verified = 0;
    let invalid = 0;
    for (let i = 0; i < prospects.length; i++) {
      const p = prospects[i];
      if (!p.email) continue;
      try {
        const res = await fetch(`${API}/api/prospects/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ email: p.email }),
        });
        const data = await res.json();
        const status = data.status;
        let emailStatus: Prospect['emailStatus'] = '';
        if (status === 'valid' || status === 'catch-all' || status === 'unknown') {
          emailStatus = 'verified'; verified++;
        } else if (status === 'risky' || status === 'do_not_mail') {
          emailStatus = 'risky'; invalid++;
        } else {
          emailStatus = 'invalid'; invalid++;
        }
        setProspects(prev => prev.map((pp, idx) =>
          idx === i ? { ...pp, emailStatus, selected: emailStatus === 'verified' } : pp
        ));
        const done = verified + invalid;
        setVerifyProgress(`Verifying ${done} of ${emailProspects.length} — ${verified} clean, ${invalid} removed...`);
      } catch (e) { console.error('Verify error:', e); continue; }
    }
    setVerifyProgress(`Done — ${verified} clean, ${invalid} removed`);
    setVerifyLoading(false);
  };

  const toggleSelect = (idx: number) => {
    const visible = emailsOnly ? prospects.filter(p => p.email) : prospects;
    const actual = prospects.indexOf(visible[idx]);
    setProspects(prev => prev.map((p, i) => i === actual ? { ...p, selected: !p.selected } : p));
  };

  const selectAll = () => setProspects(prev => prev.map(p => ({ ...p, selected: true })));
  const clearAll = () => setProspects(prev => prev.map(p => ({ ...p, selected: false })));
  const selectEmailsOnly = () => setProspects(prev => prev.map(p => ({ ...p, selected: !!p.email })));

  const clearList = () => {
    setProspects([]); setRunAllProgress(''); setRunAllProgressPct(0);
    setExported(0); setEmailsOnly(false); setVerifyProgress('');
  };

  const saveNote = (idx: number) => {
    const visible = emailsOnly ? prospects.filter(p => p.email) : prospects;
    const actual = prospects.indexOf(visible[idx]);
    setProspects(prev => prev.map((p, i) => i === actual ? { ...p, note: noteText } : p));
    setEditingNote(null); setNoteText('');
  };

  const exportCSV = () => {
    const selected = prospects.filter(p => p.selected);
    if (!selected.length) return;
    const headers = ['EMAIL', 'FIRSTNAME', 'LASTNAME', 'COMPANY', 'PHONE', 'WEBSITE', 'CATEGORY', 'REGION', 'ADDRESS', 'SOURCE', 'NOTES'];
    const rows = selected.map(p => [p.email, p.ownerFirst, p.ownerLast, p.businessName, p.phone, p.website, formatCategory(p.category), p.region, p.address, p.source, p.note || '']);
    const csv = [headers, ...rows].map(row => row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const label = runMode === 'state'
      ? STATE_METROS[selectedState]?.label.replace(/\s/g, '-').toLowerCase()
      : cities.length > 1
        ? `multi-city-${cities.length}`
        : region.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    a.download = `prospects-${label}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    const newImported = new Set(importedEmails);
    selected.forEach(p => { if (p.email) newImported.add(p.email.toLowerCase()); });
    setImportedEmails(newImported);
    setExported(prev => prev + selected.length);
  };

  const exportPhoneList = () => {
    const withPhone = prospects.filter(p => p.phone);
    if (!withPhone.length) return;
    const headers = ['COMPANY', 'PHONE', 'CATEGORY', 'REGION', 'ADDRESS'];
    const rows = withPhone.map(p => [p.businessName, p.phone, formatCategory(p.category), p.region, p.address]);
    const csv = [headers, ...rows].map(row => row.map(v => `"${(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phone-list-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const visibleProspects = emailsOnly ? prospects.filter(p => p.email) : prospects;
  const selectedCount = prospects.filter(p => p.selected).length;
  const emailCount = prospects.filter(p => p.email).length;
  const phoneCount = prospects.filter(p => p.phone).length;
  const duplicateCount = prospects.filter(p => p.isDuplicate).length;
  const categoryCount = prospects.map(p => p.category).filter((c, i, a) => a.indexOf(c) === i).length;

  const stateInfo = STATE_METROS[selectedState];

  const totalProspects = runMode === 'state'
    ? (stateInfo?.cities.length || 0) * selectedCategories.length * batchSize
    : cities.length * selectedCategories.length * batchSize;

  const getStatusBadge = (p: Prospect) => {
    if (p.isDuplicate) return <span style={styles.badgeOrange}>duplicate</span>;
    if (!p.email) return <span style={styles.badgeGray}>no email</span>;
    if (p.emailStatus === 'verified') return <span style={styles.badgeGreen}>verified</span>;
    if (p.emailStatus === 'risky') return <span style={styles.badgeYellow}>risky</span>;
    if (p.emailStatus === 'invalid') return <span style={styles.badgeRed}>invalid</span>;
    return <span style={styles.badgeBlue}>unverified</span>;
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Prospect Research</h1>
          <p style={styles.subtitle}>AI-powered service business prospecting — export directly to Brevo</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hunterCredits !== null && (
            <span style={styles.creditsBadge}>Hunter: {hunterCredits.toLocaleString()} credits</span>
          )}
          <button style={styles.navBtn} onClick={() => navigate('/admin/social')}>Social Media Manager</button>
        </div>
      </div>

      <div style={styles.notice}>
        <strong>Note:</strong> Emails found via website scraping, Hunter.io, and verified via ZeroBounce. Separate multiple cities with semicolons: <strong>Baton Rouge, LA; Denham Springs, LA; Zachary, LA</strong>
      </div>

      <div style={styles.modeToggle}>
        <button
          style={{ ...styles.modeBtn, ...(runMode === 'single' ? styles.modeBtnActive : {}) }}
          onClick={() => setRunMode('single')}
        >
          City / Multi-City
        </button>
        <button
          style={{ ...styles.modeBtn, ...(runMode === 'state' ? styles.modeBtnActive : {}) }}
          onClick={() => setRunMode('state')}
        >
          Full State (Top 5 Metros)
        </button>
      </div>

      <div style={styles.controls}>
        {runMode === 'single' ? (
          <div style={styles.controlGroup}>
            <label style={styles.label}>
              City or cities <span style={{ color: '#666', fontWeight: 400 }}>(separate multiple with semicolons)</span>
            </label>
            <textarea
              style={{ ...styles.input, minHeight: 72, resize: 'vertical', fontFamily: 'system-ui, sans-serif' }}
              value={region}
              onChange={e => setRegion(e.target.value)}
              placeholder="e.g. Baton Rouge, LA; Denham Springs, LA; Zachary, LA"
              disabled={runAllLoading}
            />
            <div style={{ marginTop: 4 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#444', margin: '0 0 6px' }}>Quick load — Louisiana metro areas:</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                <button style={{ ...styles.presetBtn, background: '#2b6cb0', color: '#fff', border: '1px solid #2b6cb0' }} onClick={() => setRegion(BR_METRO_PRESET)} disabled={runAllLoading}>
                  Load BR Metro (10 cities)
                </button>
                {SAVED_REGIONS.map(r => (
                  <button key={r} style={styles.presetBtn} onClick={() => setRegion(prev => {
                    const current = parseCities(prev).filter(c => c);
                    if (current.includes(r)) return prev;
                    return current.length === 0 ? r : prev.trimEnd() + '; ' + r;
                  })} disabled={runAllLoading}>
                    + {r.split(',')[0]}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#444', margin: '0 0 6px' }}>Load top 5 metros by state:</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                {Object.entries(STATE_METROS).map(([code, info]) => (
                  <button key={code} style={styles.presetBtn} onClick={() => setRegion(STATE_PRESETS[code])} disabled={runAllLoading}>
                    {info.label}
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#444', margin: '0 0 6px' }}>Load suburbs around top metro:</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(METRO_SUBURBS).map(([code, data]) => (
                  <button key={code} style={{ ...styles.presetBtn, color: '#276749', borderColor: '#9ae6b4', background: '#f0fff4' }} onClick={() => setRegion(data.cities.join('; '))} disabled={runAllLoading}>
                    {data.metro}, {code} suburbs
                  </button>
                ))}
              </div>
            </div>
            {isMultiCity && (
              <div style={styles.cityPills}>
                {cities.map((city, i) => (
                  <span key={i} style={{ ...styles.cityPill, background: currentCity === city ? '#2b6cb0' : '#ebf8ff', color: currentCity === city ? '#fff' : '#2b6cb0', border: currentCity === city ? '1px solid #2b6cb0' : '1px solid #bee3f8' }}>
                    {currentCity === city ? 'Running: ' : ''}{city}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div style={styles.controlGroup}>
            <label style={styles.label}>Select State</label>
            <select
              style={styles.select}
              value={selectedState}
              onChange={e => setSelectedState(e.target.value)}
              disabled={runAllLoading}
            >
              {Object.entries(STATE_METROS).map(([code, info]) => (
                <option key={code} value={code}>{info.label}</option>
              ))}
            </select>
            {stateInfo && (
              <div style={styles.cityPills}>
                {stateInfo.cities.map(city => (
                  <span key={city} style={styles.cityPill}>{city}</span>
                ))}
              </div>
            )}
          </div>
        )}
        <div style={styles.controlGroup}>
          <label style={styles.label}>Batch size per city/category</label>
          <select style={styles.select} value={batchSize} onChange={e => setBatchSize(Number(e.target.value))} disabled={runAllLoading}>
            {[5, 10, 15, 20].map(n => <option key={n} value={n}>{n} prospects</option>)}
          </select>
          {runMode === 'single' && isMultiCity && (
            <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>
              {cities.length} cities × {selectedCategories.length} categories × {batchSize} = up to <strong>{totalProspects.toLocaleString()}</strong> prospects
            </p>
          )}
        </div>
      </div>

      <div style={styles.categorySection}>
        <div style={styles.categoryHeader}>
          <div style={styles.categoryHeaderLeft}>
            <span style={styles.label}>Categories: <strong>{selectedCategories.length} of {CATEGORY_OPTIONS.length} selected</strong></span>
            <button style={styles.categoryToggleBtn} onClick={() => setShowCategoryPicker(!showCategoryPicker)}>
              {showCategoryPicker ? 'Hide' : 'Choose'}
            </button>
          </div>
          <div style={styles.categoryQuickActions}>
            <button style={styles.actionBtn} onClick={selectAllCategories}>All</button>
            <button style={styles.actionBtn} onClick={clearAllCategories}>None</button>
            <button style={styles.actionBtn} onClick={() => selectGroup('automotive')}>Automotive</button>
            <button style={styles.actionBtn} onClick={() => selectGroup('trades')}>Trades</button>
            <button style={styles.actionBtn} onClick={() => selectGroup('outdoor')}>Outdoor</button>
            <button style={styles.actionBtn} onClick={() => selectGroup('cleaning')}>Cleaning</button>
            <button style={styles.actionBtn} onClick={() => selectGroup('pet')}>Pet</button>
            <button style={styles.actionBtn} onClick={() => selectGroup('professional')}>Professional</button>
          </div>
        </div>
        {showCategoryPicker && (
          <div>
            {CATEGORY_GROUPS.map(group => (
              <div key={group.id} style={styles.groupSection}>
                <div style={styles.groupHeader}>
                  <span style={styles.groupHeaderLabel}>{group.label}</span>
                  <button style={styles.groupActionBtn} onClick={() => mergeGroup(group.id)} disabled={runAllLoading}>Select group</button>
                  <button style={styles.groupActionBtn} onClick={() => clearGroup(group.id)} disabled={runAllLoading}>Clear group</button>
                </div>
                <div style={styles.categoryGrid}>
                  {group.slugs.map(slug => (
                    <label key={slug} style={{ ...styles.categoryItem, background: selectedCategories.includes(slug) ? '#ebf8ff' : '#fff', borderColor: selectedCategories.includes(slug) ? '#2b6cb0' : '#e2e8f0' }}>
                      <input type="checkbox" checked={selectedCategories.includes(slug)} onChange={() => toggleCategory(slug)} disabled={runAllLoading} />
                      <span style={styles.categoryLabel}>{formatCategory(slug)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.runAllContainer}>
        <button
          style={runAllLoading ? styles.runAllBtnActive : selectedCategories.length === 0 ? styles.runAllBtnDisabled : styles.runAllBtn}
          onClick={runSelectedCategories}
          disabled={runAllLoading || selectedCategories.length === 0}
        >
          {runAllLoading
            ? `Running${currentCity ? ` — ${currentCity}` : ''}...`
            : runMode === 'state'
              ? `Run ${stateInfo?.label} — All 5 Cities (${selectedCategories.length} categories each)`
              : isMultiCity
                ? `Run ${cities.length} Cities — ${selectedCategories.length} categories each`
                : `Run ${selectedCategories.length === CATEGORY_OPTIONS.length ? 'All' : 'Selected'} Categories (${selectedCategories.length})`
          }
        </button>
      </div>

      {(runAllLoading || runAllProgressPct === 100) && (
        <div style={styles.progressContainer}>
          {(isMultiCity || runMode === 'state') && runAllLoading && (
            <div style={styles.cityProgress}>
              {(runMode === 'state' ? stateInfo?.cities : cities)?.map((city, i) => (
                <span key={i} style={{ ...styles.cityPill, background: currentCity === city ? '#2b6cb0' : prospects.some(p => p.region === city) ? '#e6fffa' : '#f7fafc', color: currentCity === city ? '#fff' : '#444', border: currentCity === city ? '1px solid #2b6cb0' : '1px solid #e2e8f0' }}>
                  {currentCity === city ? 'Running: ' : prospects.some(p => p.region === city) ? 'Done: ' : ''}{city.split(',')[0]}
                </span>
              ))}
            </div>
          )}
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${runAllProgressPct}%` }} />
          </div>
          <p style={styles.progressText}>{runAllProgress}</p>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {prospects.length > 0 && (
        <>
          <div style={styles.stats}>
            <div style={styles.stat}><strong>{prospects.length}</strong><span>Total generated</span></div>
            <div style={styles.stat}><strong>{emailCount}</strong><span>With emails</span></div>
            <div style={styles.stat}><strong>{phoneCount}</strong><span>With phones</span></div>
            <div style={styles.stat}><strong>{categoryCount}</strong><span>Categories</span></div>
          </div>

          {duplicateCount > 0 && (
            <div style={styles.duplicateWarning}>
              <strong>{duplicateCount} duplicates detected</strong> — these emails were in a previous export this session.
            </div>
          )}

          {emailCount > 0 && (
            <div style={styles.verifyContainer}>
              <button style={verifyLoading ? styles.verifyBtnActive : styles.verifyBtn} onClick={verifyAllEmails} disabled={verifyLoading || runAllLoading}>
                {verifyLoading ? 'Verifying...' : 'Verify All Emails Before Export'}
              </button>
              {verifyProgress && <p style={styles.verifyProgress}>{verifyProgress}</p>}
            </div>
          )}

          <div style={styles.tableControls}>
            <span style={styles.tableCount}>{visibleProspects.length} shown — {selectedCount} selected — {exported} exported</span>
            <div style={styles.tableActions}>
              <button style={styles.actionBtn} onClick={selectAll}>Select all</button>
              <button style={styles.actionBtn} onClick={clearAll}>Clear all</button>
              <button
                style={{ ...styles.actionBtn, background: emailsOnly ? '#2b6cb0' : '#fff', color: emailsOnly ? '#fff' : '#444', borderColor: '#2b6cb0' }}
                onClick={() => { setEmailsOnly(!emailsOnly); if (!emailsOnly) selectEmailsOnly(); }}
              >
                {emailsOnly ? `Emails only (${emailCount})` : `Emails only (${emailCount})`}
              </button>
              {phoneCount > 0 && (
                <button style={{ ...styles.actionBtn, color: '#276749', borderColor: '#276749' }} onClick={exportPhoneList}>
                  Phone List ({phoneCount})
                </button>
              )}
              <button style={{ ...styles.actionBtn, color: '#e53e3e', borderColor: '#e53e3e' }} onClick={clearList}>Clear list</button>
              <button style={styles.exportBtn} onClick={exportCSV} disabled={selectedCount === 0}>Export Brevo CSV</button>
            </div>
          </div>

          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}></th>
                  <th style={styles.th}>Business name</th>
                  <th style={styles.th}>Owner</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Region</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {visibleProspects.map((p, i) => (
                  <tr key={i} style={p.emailStatus === 'invalid' ? styles.trInvalid : p.isDuplicate ? styles.trDuplicate : i % 2 === 0 ? styles.trEven : styles.trOdd}>
                    <td style={styles.td}><input type="checkbox" checked={p.selected} onChange={() => toggleSelect(i)} /></td>
                    <td style={styles.td}>{p.businessName}</td>
                    <td style={styles.td}>{p.ownerFirst} {p.ownerLast}</td>
                    <td style={styles.td}>{p.email ? <a href={`mailto:${p.email}`} style={styles.emailLink}>{p.email}</a> : <span style={styles.noEmail}>—</span>}</td>
                    <td style={styles.td}>{p.phone ? <a href={`tel:${p.phone}`} style={styles.phoneLink}>{p.phone}</a> : <span style={styles.noEmail}>—</span>}</td>
                    <td style={styles.td}>{formatCategory(p.category)}</td>
                    <td style={styles.td}>{p.region}</td>
                    <td style={styles.td}>{getStatusBadge(p)}</td>
                    <td style={styles.td}>
                      {editingNote === i ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <input style={{ ...styles.input, padding: '2px 6px', fontSize: 12, width: 120 }} value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveNote(i)} autoFocus />
                          <button style={{ ...styles.actionBtn, padding: '2px 6px', fontSize: 11 }} onClick={() => saveNote(i)}>Save</button>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: p.note ? '#2d3748' : '#ccc', cursor: 'pointer' }} onClick={() => { setEditingNote(i); setNoteText(p.note || ''); }}>
                          {p.note || '+ add note'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={styles.exportHint}>Export CSV → Import into Brevo Prospect Businesses #4 → Cold Outreach sequence fires automatically</p>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '32px 24px', fontFamily: 'system-ui, sans-serif', color: '#1a202c' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  navBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  creditsBadge: { padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#276749' },
  notice: { background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14 },
  modeToggle: { display: 'flex', gap: 8, marginBottom: 16 },
  modeBtn: { padding: '10px 20px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  modeBtnActive: { background: '#2b6cb0', color: '#fff', border: '1px solid #2b6cb0' },
  controls: { display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap' },
  controlGroup: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 200 },
  label: { fontSize: 13, fontWeight: 500, color: '#444' },
  select: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, background: '#fff' },
  input: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 },
  presetBtn: { padding: '4px 10px', background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#2b6cb0' },
  cityPills: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  cityPill: { padding: '3px 10px', background: '#ebf8ff', color: '#2b6cb0', borderRadius: 99, fontSize: 12, border: '1px solid #bee3f8', transition: 'all 0.2s' },
  categorySection: { border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px', marginBottom: 16, background: '#fafafa' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  categoryHeaderLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  categoryToggleBtn: { padding: '4px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  categoryQuickActions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  categoryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginTop: 12 },
  groupSection: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' },
  groupHeader: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  groupHeaderLabel: { fontSize: 13, fontWeight: 700, color: '#2d3748', textTransform: 'uppercase', letterSpacing: 0.4 },
  groupActionBtn: { padding: '3px 10px', background: '#fff', border: '1px solid #cbd5e0', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#2b6cb0' },
  categoryItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  categoryLabel: { fontSize: 13, color: '#2d3748' },
  runAllContainer: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  runAllBtn: { padding: '12px 28px', background: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700 },
  runAllBtnActive: { padding: '12px 28px', background: '#90cdf4', color: '#1a365d', border: 'none', borderRadius: 8, cursor: 'not-allowed', fontSize: 15, fontWeight: 700 },
  runAllBtnDisabled: { padding: '12px 28px', background: '#e2e8f0', color: '#aaa', border: 'none', borderRadius: 8, cursor: 'not-allowed', fontSize: 15, fontWeight: 700 },
  progressContainer: { marginBottom: 20 },
  progressBar: { height: 10, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden', marginBottom: 8 },
  progressFill: { height: '100%', background: '#2b6cb0', borderRadius: 99, transition: 'width 0.4s ease' },
  progressText: { fontSize: 13, color: '#444', margin: 0 },
  cityProgress: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 },
  verifyContainer: { background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, padding: '16px 20px', marginBottom: 16 },
  verifyBtn: { padding: '10px 24px', background: '#276749', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700 },
  verifyBtnActive: { padding: '10px 24px', background: '#9ae6b4', color: '#276749', border: 'none', borderRadius: 8, cursor: 'not-allowed', fontSize: 14, fontWeight: 700 },
  verifyProgress: { fontSize: 13, color: '#276749', margin: '8px 0 0' },
  duplicateWarning: { background: '#fffaf0', border: '1px solid #f6ad55', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 14, color: '#7b341e' },
  error: { background: '#fff5f5', border: '1px solid #fc8181', color: '#c53030', padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 },
  stat: { background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#666' },
  tableControls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 },
  tableCount: { fontSize: 14, color: '#444' },
  tableActions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  actionBtn: { padding: '6px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  exportBtn: { padding: '8px 18px', background: '#1a202c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  tableWrapper: { overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 600, color: '#444', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
  trEven: { background: '#fff' },
  trOdd: { background: '#fafafa' },
  trInvalid: { background: '#fff5f5', opacity: 0.6 },
  trDuplicate: { background: '#fffaf0' },
  emailLink: { color: '#2b6cb0', textDecoration: 'none' },
  phoneLink: { color: '#276749', textDecoration: 'none' },
  noEmail: { color: '#aaa' },
  badgeGreen: { background: '#e6fffa', color: '#234e52', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  badgeBlue: { background: '#ebf8ff', color: '#2b6cb0', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  badgeYellow: { background: '#fffff0', color: '#b7791f', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  badgeRed: { background: '#fff5f5', color: '#c53030', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  badgeGray: { background: '#f7fafc', color: '#aaa', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  badgeOrange: { background: '#fffaf0', color: '#c05621', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 },
  exportHint: { textAlign: 'center', fontSize: 12, color: '#999', marginTop: 12 },
};
